import { Injectable } from '@angular/core';
import { BasicResponse } from 'src/app/core/models/basic-response.model';
import { getEndpoint } from 'src/app/core/services/functions.service';
import { RequestsService } from 'src/app/core/services/requests.service';
import {
  descargarPorTramos,
  Progreso,
  RangoFechas,
  ResultadoTramo
} from '../../utils/tramos-reporte';

export { RangoFechas } from '../../utils/tramos-reporte';

/** Mensaje cuando el rango completo no tiene transacciones */
const SIN_DATOS = 'No hay datos para el rango de fechas seleccionado.';

/**
 * Servicio único del módulo de Reportes.
 *
 * Como en `AuditoriaService`, las urls base se resuelven desde ser_endpoints
 * (Superadmin > Config > Endpoints) y aquí se les concatena la ruta. No hay
 * valor de respaldo en el código: si la clave falta se avisa, para que un
 * error de configuración no termine apuntando al ambiente equivocado.
 *
 *   reportesEpayco     -> url base de la lambda de reportes
 *   reportesEpaycoTok  -> url base de la lambda que entrega el token
 *
 * Ambas claves aceptan la base (`.../dev/`) o la url completa del recurso
 * (`.../dev/reportes/epayco-credijamar`, `.../token`): si la ruta ya viene en
 * el valor no se concatena de nuevo.
 *
 * Autenticación: la lambda de reportes exige un token que entrega otra lambda.
 * Se pide una vez al entrar al módulo y se reutiliza en memoria; si un tramo
 * falla con el token en memoria y el backend no devolvió un error de dominio,
 * se refresca y se reintenta una sola vez.
 *
 *  POST {reportesEpaycoTok}token                     Token vigente (sin body)
 *  POST {reportesEpaycoTok}token?force_refresh=true  Fuerza uno nuevo (sin body)
 *       -> se acepta { access_token } | { token } | { data: { access_token | token } },
 *          con `expires_in` opcional en segundos
 *
 * Contrato del reporte:
 *  POST {reportesEpayco}reportes/epayco-credijamar
 *       Authorization: Bearer {token}
 *       body { fechaInicio: "YYYY-MM-DD", fechaFin: "YYYY-MM-DD" }
 *       Responde el CSV como binario (UTF-8 con BOM, separado por `;`). Si
 *       falla responde JSON { error, message }: `recurso no encontrado` (404)
 *       cuando no hay transacciones y `rango de fechas demasiado extenso`
 *       (400) cuando el rango no cabe en una respuesta. También se acepta un
 *       JSON con una url de descarga ({ url } o { data: { url } }).
 *
 * El backend solo acepta rangos de unos 15 días, así que el rango del usuario
 * se parte en tramos (ver `utils/tramos-reporte.ts`) y los CSV se unen aquí.
 */
@Injectable({ providedIn: 'root' })
export class ReportesService {

  /** Token vigente y el instante (ms) en que deja de servir (0 = sin vencimiento conocido) */
  private accessToken = '';
  private tokenExpiresAt = 0;

  constructor(private requestsService: RequestsService) { }

  /**
   * Resuelve una clave de ser_endpoints. Devuelve cadena vacía si no está
   * creada, para que el llamador muestre el aviso de configuración faltante.
   * @param clave nombre del endpoint en ser_endpoints
   */
  private resolve(clave: string): string {
    try {
      return getEndpoint(clave) || '';
    } catch (error) {
      return '';
    }
  }

  /**
   * Garantiza la barra final de una url base
   * @param base url base
   */
  private withSlash(base: string): string {
    return !base || base.endsWith('/') ? base : `${base}/`;
  }

  /**
   * Concatena la ruta a la base, salvo que el endpoint ya se haya guardado con
   * la url completa: así sirve tanto `.../dev/` como `.../dev/reportes/epayco-credijamar`
   * @param base url base con barra final
   * @param path ruta del recurso, sin barra inicial
   */
  private joinPath(base: string, path: string): string {
    const trimmed = base.replace(/\/+$/, '');
    return trimmed.endsWith(`/${path}`) ? trimmed : `${trimmed}/${path}`;
  }

  /** Url base de la lambda de reportes */
  private get baseReportes(): string {
    return this.withSlash(this.resolve('reportesEpayco'));
  }

  /** Url base de la lambda que entrega el token */
  private get baseToken(): string {
    return this.withSlash(this.resolve('reportesEpaycoTok'));
  }

  /** true si hay un token en memoria que todavía sirve */
  private get hasValidToken(): boolean {
    return !!this.accessToken && (this.tokenExpiresAt === 0 || Date.now() < this.tokenExpiresAt);
  }

  /**
   * Pide el token por adelantado, para que la primera descarga no tenga que
   * esperarlo. Se llama al entrar al módulo.
   */
  async prepararSesion(): Promise<BasicResponse> {
    return await this.getAccessToken();
  }

  /**
   * Devuelve un token vigente. Reutiliza el que está en memoria y solo vuelve
   * a la lambda cuando no hay, está por vencer o se pide refrescarlo.
   * @param forceRefresh true para pedir uno nuevo con force_refresh=true
   */
  private async getAccessToken(forceRefresh = false): Promise<BasicResponse> {
    if (!forceRefresh && this.hasValidToken) {
      return new BasicResponse(true, 'Token vigente', this.accessToken);
    }
    const base = this.baseToken;
    if (!base) {
      return new BasicResponse(false, 'No está configurada la url de la lambda de token (clave reportesEpaycoTok).');
    }
    //POST sin body; el refresco va como parámetro en la url
    const result = await this.requestsService.postLikeJSON(
      `${this.joinPath(base, 'token')}${forceRefresh ? '?force_refresh=true' : ''}`, {}
    );
    const token = this.extractToken(result.data);
    if (!result.success || !token) {
      this.accessToken = '';
      this.tokenExpiresAt = 0;
      return new BasicResponse(false, result.message || 'No pudimos obtener el token para el reporte.');
    }
    //Se descuenta un minuto para no usar un token que vence durante la petición
    const expiresIn = Number(result.data?.expires_in ?? result.data?.data?.expires_in);
    this.accessToken = token;
    this.tokenExpiresAt = expiresIn > 0 ? Date.now() + Math.max(expiresIn - 60, 0) * 1000 : 0;
    return new BasicResponse(true, 'Token obtenido', token);
  }

  /**
   * Saca el token de la respuesta de la lambda, sea cual sea el nombre con que
   * lo envíe
   * @param data cuerpo de la respuesta
   */
  private extractToken(data: any): string {
    if (!data) {
      return '';
    }
    if (typeof data === 'string') {
      return data;
    }
    return data.access_token || data.token || data.data?.access_token || data.data?.token || '';
  }

  /**
   * Genera el reporte epayco-credijamar para el rango completo que pidió el
   * usuario. El rango se parte en tramos que se piden en secuencia y los CSV
   * se unen en un solo archivo. En `data` viene el `Blob` listo para descargar.
   * @param body rango completo en formato YYYY-MM-DD
   * @param onProgreso aviso de avance: cuántos tramos van y cuántos hay
   */
  async descargarEpaycoCredijamar(body: RangoFechas, onProgreso?: Progreso): Promise<BasicResponse> {
    const base = this.baseReportes;
    if (!base) {
      return new BasicResponse(false, 'No está configurada la url de la lambda de reportes (clave reportesEpayco).');
    }
    const url = this.joinPath(base, 'reportes/epayco-credijamar');
    const resultado = await descargarPorTramos(body, tramo => this.descargarTramo(url, tramo), onProgreso);
    if (!resultado.ok) {
      return new BasicResponse(false, resultado.sinDatos ? SIN_DATOS : resultado.message);
    }
    const blob = new Blob([resultado.csv], { type: 'text/csv;charset=utf-8' });
    return new BasicResponse(true, 'Reporte generado', blob);
  }

  /**
   * Pide un tramo al backend y lo deja en la forma que espera
   * `descargarPorTramos`: el texto del CSV, o la clave y el mensaje de error.
   * Si falló con un token que venía de memoria y el backend no devolvió un
   * error de dominio (los de dominio traen `error`), se refresca el token y se
   * reintenta una sola vez. RequestsService no conserva el status, así que un
   * 401 solo se reconoce por la ausencia de esa clave.
   * @param url url completa del reporte
   * @param tramo fechas del tramo
   */
  private async descargarTramo(url: string, tramo: RangoFechas): Promise<ResultadoTramo> {
    const usedCachedToken = this.hasValidToken;
    let result = await this.postReporte(url, tramo, false);
    let cuerpo = result.success ? null : await this.leerCuerpoError(result);
    if (cuerpo && usedCachedToken && !cuerpo.error) {
      result = await this.postReporte(url, tramo, true);
      cuerpo = result.success ? null : await this.leerCuerpoError(result);
    }
    if (cuerpo) {
      return { ok: false, error: cuerpo.error, message: cuerpo.message };
    }
    if (result.data instanceof Blob && result.data.size === 0) {
      //Respuesta vacía: el tramo no aporta filas pero no es un error
      return { ok: true, csv: '' };
    }
    const archivo = await this.toFileBlob(result.data);
    if (!archivo.success) {
      return { ok: false, error: '', message: archivo.message };
    }
    return { ok: true, csv: await this.leerTexto(archivo.data) };
  }

  /**
   * Hace el POST del reporte con un token vigente
   * @param url url completa del reporte
   * @param body fechas del tramo
   * @param forceRefresh true para refrescar el token antes de llamar
   */
  private async postReporte(url: string, body: RangoFechas, forceRefresh: boolean): Promise<BasicResponse> {
    const token = await this.getAccessToken(forceRefresh);
    if (!token.success) {
      return token;
    }
    return await this.requestsService.postLikeBLOB(
      url, body, { Authorization: `Bearer ${token.data}` }, 'blob'
    );
  }

  /**
   * Lee el CSV conservando el BOM. `Blob.text()` lo quitaría, y el primer
   * tramo lo necesita para que Excel abra el archivo con la codificación correcta.
   * @param blob archivo del tramo
   */
  private async leerTexto(blob: Blob): Promise<string> {
    return new TextDecoder('utf-8', { ignoreBOM: true }).decode(await blob.arrayBuffer());
  }

  /**
   * Normaliza la respuesta a un Blob de archivo. Si el backend respondió JSON
   * en vez de binario, se interpreta: una url de descarga se va a buscar, y
   * un `success: false` se convierte en error.
   * @param data respuesta cruda del backend
   */
  private async toFileBlob(data: any): Promise<BasicResponse> {
    if (!(data instanceof Blob)) {
      return new BasicResponse(false, 'El backend no devolvió un archivo.');
    }
    if (data.size === 0) {
      return new BasicResponse(false, SIN_DATOS);
    }
    if (!data.type.includes('json')) {
      return new BasicResponse(true, 'Reporte generado', data);
    }
    let json: any;
    try {
      json = JSON.parse(await data.text());
    } catch (error) {
      return new BasicResponse(false, 'No pudimos interpretar la respuesta del backend.');
    }
    if (json.success === false) {
      return new BasicResponse(false, json.message || SIN_DATOS);
    }
    const downloadUrl = json.url || json.data?.url;
    if (downloadUrl) {
      //Una url de descarga (ej. S3 prefirmada) no lleva token: rechazaría la petición
      const file = await this.requestsService.getBLOB(downloadUrl, {}, undefined, 'blob');
      if (!file.success) {
        return new BasicResponse(false, (await this.leerCuerpoError(file)).message);
      }
      return await this.toFileBlob(file.data);
    }
    return new BasicResponse(false, json.message || 'El backend no devolvió un archivo.');
  }

  /**
   * Con responseType blob los errores HTTP llegan con el cuerpo como Blob; se
   * leen la clave `error` y el `message` que manda el backend. Si el cuerpo no
   * es el JSON del backend (p. ej. un 401 del API Gateway) `error` queda vacío
   * y el mensaje es el genérico.
   * @param result respuesta fallida de RequestsService
   */
  private async leerCuerpoError(result: BasicResponse): Promise<{ error: string, message: string }> {
    const generico = result.message || 'No pudimos generar el reporte.';
    //getBasicError ya deja en `error` el cuerpo de la respuesta
    const body = result.error;
    if (body instanceof Blob) {
      try {
        const text = await body.text();
        const json = JSON.parse(text);
        return {
          error: typeof json.error === 'string' ? json.error : '',
          message: json.message || json.error || text || generico
        };
      } catch (error) {
        //El cuerpo no era JSON: se usa el mensaje genérico
      }
    }
    return { error: '', message: generico };
  }
}
